
import React, { useEffect, useState } from "react";
import "./LeadDetails.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";

interface TimelineItem {
  id: string;
  type: string;
  agent_name: string | null;
  role_id?: number | null;
  timeline_time: string;
  otherDetails: any; // Will parse from JSON string
  profile_image: string | null;
  time_diff: string;
}

interface RawLeadData {
  id: number;
  name: string;
  email: string | null;
  number: string;
  remarks: string | null;
  voice_note: string | null;
  recording_link: string | null;
  call_duration?: string; // Added call_duration field
  customer_address: string | null;
  buying_location: string | null;
  property_type: string | null;
  configuration: string | null;
  budget_min: string | null;
  budget_max: string | null;
  created_at: string;
  [key: string]: any;
}

interface LeadData {
  rawLead: RawLeadData;
  timeline: TimelineItem[];
  leadId: string | null;
  inquiriesCount: number;
}

const RawLeadDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  
  
  const {
    raw_lead_id,
    raw_leads_remarks,
    customer_address,
    raw_leads_buying_location,
    raw_leads_property_type,
    raw_leads_configuration,
    raw_leads_budget_max,
    raw_leads_budget_min,
    bhk,
    city,
    voice_note,
    recording_link
  } = location.state || {};

  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          alert("Session expired, please login again");
          navigate("/");
          return;
        }

        const res = await axios.get(
          `https://www.crm.brickfolio.in/api/global-partner-connect/raw-leads-history/${raw_lead_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        
        console.log("API Response:", res.data); // Debug log
        
        // Parse the timeline otherDetails from JSON string to object
        const parsedData = {
          ...res.data.data,
          timeline: res.data.data.timeline.map((item: TimelineItem) => {
            let parsedDetails = {};
            try {
              parsedDetails = typeof item.otherDetails === 'string' 
                ? JSON.parse(item.otherDetails) 
                : item.otherDetails || {};
              
              // Log the parsed details to see the structure
              console.log(`Parsed details for ${item.type}:`, parsedDetails);
            } catch (error) {
              console.error("Error parsing otherDetails:", error);
              parsedDetails = {};
            }
            
            return {
              ...item,
              otherDetails: parsedDetails,
              time_diff: calculateTimeDiff(item.timeline_time) // Calculate time difference
            };
          })
        };
        
        console.log("Parsed Data:", parsedData); // Debug log
        setLeadData(parsedData);
      } catch (error: any) {
        console.error("Error fetching lead details:", error);

        if (error?.response?.status === 401) {
          localStorage.removeItem("authToken");
          alert("Session expired, please login again");
          navigate("/");
        } else {
          alert("Cannot fetch lead details");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeadDetails();
  }, [id, navigate]);

  // Function to calculate time difference
  const calculateTimeDiff = (timelineTime: string): string => {
    try {
      const now = new Date();
      const past = new Date(timelineTime);
      const diffMs = now.getTime() - past.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return `${diffDays} days ago`;
    } catch (error) {
      return "Recently";
    }
  };

  const getRoleDisplayName = (roleId?: number | null): string => {
    switch (roleId) {
      case 4:
        return "Sales Agent";
      case 5:
        return "Presales Agent";
      default:
        return "System Generated";
    }
  };

  if (loading) return <p className="loadingText">Loading lead details...</p>;
  if (!leadData) return <p className="loadingText">No lead data found</p>;

  // Get lead information from the first timeline item
  const firstTimelineItem = leadData.timeline[0];
  const clientName = firstTimelineItem?.otherDetails?.client_name || leadData.rawLead?.name || "Unknown Client";

  // Helper function to get recording link from otherDetails
  const getRecordingLink = (otherDetails: any): string | null => {
    if (!otherDetails) return null;
    
    // Check for recording field (from your example)
    if (otherDetails.recording) {
      return otherDetails.recording;
    }
    
    // Check for recording_link field (fallback)
    if (otherDetails.recording_link) {
      return otherDetails.recording_link;
    }
    
    return null;
  };

  // Helper function to get recording time
  const getRecordingTime = (otherDetails: any): string | null => {
    if (!otherDetails) return null;
    
    if (otherDetails.recording_time) {
      return otherDetails.recording_time;
    }
    
    return null;
  };

  const formatTimelineItem = (item: TimelineItem) => {
    const { type, timeline_time, otherDetails, time_diff, role_id } = item;

    const roleDisplayName = getRoleDisplayName(role_id);
    const recordingLink = getRecordingLink(otherDetails);
    const recordingTime = getRecordingTime(otherDetails);
    const hasRecording = !!recordingLink;

    switch (type) {
      case "leadgen":
        return {
          icon: "📥",
          title: "Lead Generated",
          description: `Source: ${otherDetails.source_of_lead || "Unknown"}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "system_call":
        return {
          icon: "📞",
          title: "Call Attempted",
          description: `${otherDetails.cust_status || "System call was made to client"}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "sales_recording_call":
        return {
          icon: "🎙️",
          title: "Sales Call",
          description: `Sales call ${recordingTime ? `at ${recordingTime}` : "completed"}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "recording":
        return {
          icon: "🎵",
          title: "Recording Added",
          description: otherDetails.remark || `Recording ${recordingTime ? `at ${recordingTime}` : "added"}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "lead":
        return {
          icon: "📋",
          title: "Lead Details Updated",
          description: otherDetails.requirement || "Lead requirements updated",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "meeting":
        return {
          icon: "📅",
          title: "Meeting Scheduled",
          description: `Meeting at ${otherDetails.address || "Unknown location"} on ${otherDetails.meeting_date} ${otherDetails.meeting_time}`,
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "activity":
        return {
          icon: "⚡",
          title: "Activity",
          description: otherDetails.activity || "System activity",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      case "disposition":
        return {
          icon: "🏷️",
          title: otherDetails.despositions_name || "Disposition Updated",
          description: otherDetails.remark || "Status updated",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };

      default:
        return {
          icon: "🔹",
          title: type,
          description: "Activity performed",
          time: timeline_time,
          role: roleDisplayName,
          timeDiff: time_diff,
          hasRecording,
          recordingLink,
          recordingTime,
          agentName: item.agent_name
        };
    }
  };

  // For raw leads, check if there's any disposition in timeline, otherwise it's "New Lead"
  const latestDisposition = leadData.timeline
    .filter(item => item.type === "disposition")
    .pop();

  const currentStatus = latestDisposition?.otherDetails?.despositions_name || "New Lead";

  // Use data from API response first, fallback to location.state
  const remarks = leadData.rawLead?.remarks || raw_leads_remarks || "N/A";
  const customerAddress = leadData.rawLead?.customer_address || customer_address || "N/A";
  const buyingLocation = leadData.rawLead?.buying_location || raw_leads_buying_location || "N/A";
  const propertyType = leadData.rawLead?.property_type || raw_leads_property_type || "N/A";
  const configuration = leadData.rawLead?.configuration || raw_leads_configuration || "N/A";
  const budgetMin = leadData.rawLead?.budget_min || raw_leads_budget_min || "N/A";
  const budgetMax = leadData.rawLead?.budget_max || raw_leads_budget_max || "N/A";
  const voiceNote = leadData.rawLead?.voice_note || voice_note || null;
  
  // Find all recordings from timeline
  const timelineRecordings = leadData.timeline
    .map(item => ({
      recordingLink: getRecordingLink(item.otherDetails),
      recordingTime: getRecordingTime(item.otherDetails),
      type: item.type,
      timelineTime: item.timeline_time,
      agentName: item.agent_name
    }))
    .filter(item => item.recordingLink);

  // Get the first recording for the main section
  const mainRecording = timelineRecordings.length > 0 ? timelineRecordings[0] : null;
  
  // Also check rawLead for recording_link
  const rawLeadRecording = leadData.rawLead?.recording_link || recording_link;

  // Extract BHK from configuration if available
  const extractedBHK = configuration || bhk || "N/A";

  return (
    <div className="leadPage">
      <div className="leadDetailsContainer">
        <div className="leadHeader">
          <div className="headerTop" onClick={() => navigate("/dashboard")}>
            ← Back to Leads
          </div>

          <div className="leadProfile">
            <div className="avatar">👤</div>
            <div className="profileInfo">
              <h2>{clientName}</h2>
              <button className={`followBtn ${currentStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                {currentStatus}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="contentContainer">
        {/* Contact Information */}
        <div className="cardBox">
          <h3>Contact Information</h3>
          <div className="grid3">
            <div className="infoItem">
              <label>Lead ID</label>
              <p>{leadData.rawLead?.id || raw_lead_id || "N/A"}</p>
            </div>

            <div className="infoItem">
              <label>BHK</label>
              <p>{extractedBHK}</p>
            </div>
            <div className="infoItem">
              <label>City</label>
              <p>{city || "N/A"}</p>
            </div>
            <div className="infoItem">
              <label>Property Type</label>
              <p>{propertyType}</p>
            </div>
            <div className="infoItem">
              <label>Configuration</label>
              <p>{configuration}</p>
            </div>
            <div className="infoItem">
              <label>Buying Location</label>
              <p>{buyingLocation}</p>
            </div>
            <div className="infoItem fullWidth">
              <label>Budget Range</label>
              <p>
                {budgetMin !== "N/A" && budgetMax !== "N/A"
                  ? `₹${budgetMin} - ₹${budgetMax} Lakhs`
                  : "N/A"}
              </p>
            </div>
            <div className="infoItem fullWidth">
              <label>Customer Address</label>
              <p>{customerAddress}</p>
            </div>
            <div className="infoItem fullWidth">
              <label>Remarks</label>
              <p>{remarks}</p>
            </div>
          </div>
        </div>

        {/* Property Requirements - For Raw Leads, show from remarks or timeline */}
        <div className="cardBox">
          <h3>Property Requirements</h3>
          <div className="grid2">
            <div>
              <label>📍 Property Type</label>
              <p>{propertyType}</p>
            </div>
            <div>
              <label>💰 Budget</label>
              <p>
                {budgetMin !== "N/A" && budgetMax !== "N/A"
                  ? `₹${budgetMin} - ₹${budgetMax} Lakhs`
                  : "N/A"}
              </p>
            </div>
            <div>
              <label>🏠 Configuration</label>
              <p>{configuration}</p>
            </div>
            <div>
              <label>📍 Buying Location</label>
              <p>{buyingLocation}</p>
            </div>
            {remarks && remarks !== "N/A" && (
              <div className="fullWidth">
                <label>📝 Requirement Details</label>
                <p>{remarks}</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline - Exactly like LeadDetails */}
        <div className="cardBox">
          <h3>Timeline</h3>
          <div className="timelineContainer">
            {leadData.timeline.length === 0 ? (
              <div className="noTimeline">
                <p>No activity recorded yet.</p>
              </div>
            ) : (
              leadData.timeline.map((item, index) => {
                const formattedItem = formatTimelineItem(item);
                
                return (
                  <div key={`${item.id}-${index}`} className="timelineRow">
                    {/* vertical line */}
                    {index !== 0 && <div className="timelineLine"></div>}

                    <div className="timelineIcon">
                      {formattedItem.icon}
                    </div>

                    <div className="timelineContent">
                      <div className="timelineHeader">
                        <h4 className="timelineTitle">{formattedItem.title}</h4>
                        <span className="timelineTime">
                          {new Date(formattedItem.time).toLocaleDateString()} •{" "}
                          {new Date(formattedItem.time).toLocaleTimeString([], { 
                            hour: "2-digit", 
                            minute: "2-digit" 
                          })}
                        </span>
                      </div>

                      <p className="timelineDescription">{formattedItem.description}</p>

                      {/* Show agent name if available */}
                      {formattedItem.agentName && (
                        <p className="timelineAgentName">
                          👤 {formattedItem.agentName}
                        </p>
                      )}

                      {/* Show recording in timeline if available */}
                      {formattedItem.hasRecording && formattedItem.recordingLink && (
                        <div className="timeline-recording-section">
                          <a 
                            href={formattedItem.recordingLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="timeline-recording-link"
                          >
                            {/* <span className="recording-icon">🎧</span>
                            <span>Listen to Recording</span> */}
                            {/* {formattedItem.recordingTime && (
                              <span className="recording-time-small">({formattedItem.recordingTime})</span>
                            )} */}
                          </a>
                        </div>
                      )}

                      <div className="timelineFooter">
                        <p className="timelineAgent">
                          {formattedItem.role}
                        </p>
                        <span className="timelineTimeDiff">
                          {formattedItem.timeDiff}
                        </span>
                      </div>

                      {/* Show additional details for leadgen */}
                      {item.type === "leadgen" && item.otherDetails && (
                        <div className="timelineExtraDetails">
                          <p><strong>Client:</strong> {item.otherDetails.client_name}</p>
                          {item.otherDetails.generated_for_project && (
                            <p><strong>Project:</strong> {item.otherDetails.generated_for_project}</p>
                          )}
                          {item.otherDetails.remarks && (
                            <p><strong>Remarks:</strong> {item.otherDetails.remarks}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawLeadDetails;